/**
 * Séparation des revenus par ENTITÉ — /app/separation-revenus
 * Fondation (dons/caritatif) vs Impact Kick-Boxing inc. (commercial).
 * Lecture seule + audit + rapport de contrôle.
 */
import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Bank, ShieldCheck, Warning, ArrowClockwise, CircleNotch, CheckCircle, XCircle } from "@phosphor-icons/react";

export default function SeparationRevenus() {
  const [config, setConfig] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [auditing, setAuditing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, r] = await Promise.all([
        api.get("/entities/config"),
        api.get("/entities/report"),
      ]);
      setConfig(c.data);
      setReport(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de chargement");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function runAudit() {
    setAuditing(true);
    try {
      const r = await api.post("/entities/audit");
      toast.success(`${r.data.classes_compagnie} produit(s) classé(s) → Impact Kick-Boxing inc.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur d'audit"); }
    setAuditing(false);
  }

  if (loading) {
    return <div className="flex items-center justify-center py-32"><CircleNotch size={32} className="animate-spin text-zinc-400" /></div>;
  }

  const badProducts = report?.alertes?.produits_commerciaux_sur_fondation || [];
  const unclassified = report?.alertes?.produits_sans_entite || 0;

  return (
    <div className="max-w-5xl mx-auto p-6" data-testid="separation-revenus-page">
      <div className="flex items-center gap-3 mb-1">
        <Bank size={26} weight="fill" className="text-zinc-800" />
        <h1 className="text-2xl font-bold">Séparation des revenus par entité</h1>
      </div>
      <p className="text-sm text-zinc-500 mb-6">Fondation (dons & caritatif) vs Impact Kick-Boxing inc. (commercial). Aucun fallback automatique vers la fondation.</p>

      {/* Bandeau mode */}
      <div className={`rounded-lg p-4 mb-6 border ${config?.mode === "strict" ? "bg-emerald-50 border-emerald-300" : "bg-amber-50 border-amber-300"}`} data-testid="mode-banner">
        <div className="flex items-center gap-2 font-bold">
          {config?.mode === "strict" ? <ShieldCheck size={20} className="text-emerald-600" /> : <Warning size={20} className="text-amber-600" />}
          Mode : {config?.mode === "strict" ? "ROUTAGE STRICT actif" : "TRANSITION"}
        </div>
        {config?.transition_warning && <p className="text-sm text-amber-800 mt-1.5">{config.transition_warning}</p>}
      </div>

      {/* Cartes entités */}
      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        {(config?.entities || []).map((e) => (
          <div key={e.id} className="border border-zinc-200 rounded-lg p-5" data-testid={`entity-card-${e.id}`} style={{ borderTopColor: e.badge_color, borderTopWidth: 3 }}>
            <div className="font-bold text-lg">{e.label}</div>
            <div className="text-xs text-zinc-500">{e.legal_name}</div>
            <div className="text-xs text-zinc-500 mt-2">{e.bank_label}</div>
            <div className="mt-3 flex items-center gap-1.5 text-sm font-semibold">
              {e.stripe_configured
                ? <><CheckCircle size={16} weight="fill" className="text-emerald-500" /> Compte Stripe configuré</>
                : <><XCircle size={16} weight="fill" className="text-red-500" /> Compte Stripe NON configuré</>}
            </div>
          </div>
        ))}
      </div>

      {/* Alertes */}
      {(badProducts.length > 0 || unclassified > 0) && (
        <div className="bg-red-50 border border-red-300 rounded-lg p-4 mb-6" data-testid="alerts-box">
          <div className="font-bold text-red-700 flex items-center gap-2"><Warning size={18} weight="fill" /> Alertes de classification</div>
          {unclassified > 0 && <p className="text-sm text-red-700 mt-1">{unclassified} produit(s) sans entité définie.</p>}
          {badProducts.length > 0 && (
            <p className="text-sm text-red-700 mt-1">{badProducts.length} produit(s) commercial(aux) encore reliés à la Fondation : {badProducts.map((p) => p.name).join(", ")}</p>
          )}
          <button onClick={runAudit} disabled={auditing} data-testid="run-audit-btn"
            className="mt-3 inline-flex items-center gap-2 bg-red-600 text-white text-sm font-semibold px-4 py-2 rounded hover:bg-red-700 disabled:opacity-50">
            {auditing ? <CircleNotch size={15} className="animate-spin" /> : <ArrowClockwise size={15} />} Reclasser les produits → Impact Kick-Boxing inc.
          </button>
        </div>
      )}

      {/* Rapport de contrôle */}
      <div className="border border-zinc-200 rounded-lg p-5" data-testid="control-report">
        <h2 className="font-bold mb-3">Rapport de contrôle (revenus payés)</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <ReportBlock title="Boutique / Équipement" data={report?.volume_par_entite?.boutique} />
          <ReportBlock title="Adhésions" data={report?.volume_par_entite?.adhesions} />
        </div>
        <div className="mt-4 flex gap-2">
          <button onClick={load} className="text-sm border border-zinc-200 rounded px-4 py-2 hover:bg-zinc-50 flex items-center gap-1.5"><ArrowClockwise size={15} /> Rafraîchir</button>
          <button onClick={runAudit} disabled={auditing} className="text-sm border border-zinc-200 rounded px-4 py-2 hover:bg-zinc-50" data-testid="audit-btn-2">Lancer un audit</button>
        </div>
      </div>
    </div>
  );
}

function ReportBlock({ title, data }) {
  const f = data?.foundation || { count: 0, total: 0 };
  const c = data?.company || { count: 0, total: 0 };
  const n = data?.non_classe || { count: 0, total: 0 };
  return (
    <div className="bg-zinc-50 rounded p-4">
      <div className="text-xs uppercase tracking-wide text-zinc-500 font-semibold mb-2">{title}</div>
      <Row label="Impact Kick-Boxing inc." color="#D90429" count={c.count} total={c.total} />
      <Row label="Fondation" color="#7C3AED" count={f.count} total={f.total} />
      {n.count > 0 && <Row label="Non classé" color="#999" count={n.count} total={n.total} />}
    </div>
  );
}

function Row({ label, color, count, total }) {
  return (
    <div className="flex items-center justify-between py-1 text-sm">
      <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />{label}</span>
      <span className="font-semibold">{Number(total || 0).toLocaleString("fr-CA")} $ <span className="text-zinc-400 font-normal">({count})</span></span>
    </div>
  );
}
